import { Hatch, HatchMap, SafeString } from "./classes/Hatch";
import Loader from "./components/Loader";
import Tooltip from "./components/Tooltip";

import no_luggage from "../../static/svg/icons/no_luggage.svg";
import warning from "../../static/svg/icons/warning.svg";
import close from "../../static/svg/icons/close.svg";
import add from "../../static/svg/icons/add.svg";
import remove from "../../static/svg/icons/remove.svg";
import firstPage from "../../static/svg/icons/first_page.svg";
import chevronLeft from "../../static/svg/icons/chevron_left.svg";
import chevronRight from "../../static/svg/icons/chevron_right.svg";
import lastPage from "../../static/svg/icons/last_page.svg";

const LOG: HTMLElement = document.querySelector("#log");
let EVENTS;
let HREF = new URL(window.location.href);
let LIMIT = parseInt(HREF.searchParams.get("limit")) || 100;
let view = localStorage.getItem("analog-view-preference") ? localStorage.getItem("analog-view-preference") : "rows";

let LOADER: Loader;

let offset = parseInt(HREF.searchParams.get("offset")) || 0;

interface Entry {
  n: string;
  d: any;
}

interface Counter {
  label: string;
  counters: any;
}

interface Filter {
  key: string;
  q: string;
  value: string;
  event: string;
}

let reMap = {
  "analog.header": "Misc",

  "net.flow.end": "Network",
  "net.flow.tls": "Network",
  "net.dns.req": "Network",
  "net.dns.resp": "Network",
  "net.http.req": "Network",
  "net.http.resp": "Network",
  "net.ftp.cred": "Network",
  "net.smtp.cred": "Network",
  "net.flow.proc": "Network",
  "net.flow.start": "Network",

  "windows.proc.create": "Process",
  "windows.proc.terminate": "Process",
  "windows.reg.read": "Registry",
  "windows.reg.create": "Registry",
  "windows.reg.write": "Registry",
  "windows.reg.delete": "Registry",
  "windows.mutant": "Mutex",
  "windows.event": "Mutex",
  "windows.file.create": "Files",
  "windows.file.delete": "Files",
  "windows.file.rename": "Files",
  "windows.file.write": "Files",
  "windows.file.read": "Files",

  "linux.proc.create": "Process",
  "linux.proc.terminate": "Process",
  "linux.file.create": "Files",
  "linux.file.read": "Files",
  "linux.file.write": "Files",
  "linux.file.delete": "Files",
  "linux.file.rename": "Files",

  "android.file.create": "Files",
  "android.file.read": "Files",
  "android.file.write": "Files",
  "android.file.delete": "Files",
  "android.file.rename": "Files",
  "android.proc.terminate": "Process",
  "android.proc.create": "Process",
  "android.preference.get": "Shared Preferences",
  "android.preference.put": "Shared Preferences",
  "android.resolver.query": "Content URI",
  "android.resolver.insert": "Content URI",
  "android.resolver.delete": "Content URI",
  "android.resolver.observe": "Content URI",
  "android.service": "System Service",

  "macos.file.create": "Files",
  "macos.file.read": "Files",
  "macos.file.write": "Files",
  "macos.file.delete": "Files",
  "macos.file.rename": "Files",

  "proc.attack": "Attack",
};

let nameMap = {
  "analog.header": "Analog Header",

  "net.dns.req": "DNS Request",
  "net.dns.resp": "DNS Response",
  "net.http.req": "HTTP Request",
  "net.http.resp": "HTTP Response",
  "net.ftp.cred": "FTP Credentials",
  "net.smtp.cred": "SMTP Credentials",
  "net.flow.proc": "Flow Process",
  "net.flow.start": "Flow Start",
  "net.flow.end": "Flow End",
  "net.flow.tls": "Flow TLS",

  "windows.proc.create": "Process Create",
  "windows.proc.terminate": "Process Terminate",
  "windows.reg.read": "Registry Read",
  "windows.reg.create": "Registry Create",
  "windows.reg.write": "Registry Write",
  "windows.reg.delete": "Registry Delete",
  "windows.mutant": "Mutant",
  "windows.event": "Event",
  "windows.file.create": "File Create",
  "windows.file.delete": "File Delete",
  "windows.file.rename": "File Rename",
  "windows.file.write": "File Write",
  "windows.file.read": "File Read",

  "linux.proc.create": "Process Create",
  "linux.proc.terminate": "Process Terminate",
  "linux.file.create": "File Create",
  "linux.file.read": "File Read",
  "linux.file.write": "File Write",
  "linux.file.delete": "File Delete",
  "linux.file.rename": "File Rename",

  "android.file.create": "File Create",
  "android.file.read": "File Read",
  "android.file.write": "File Write",
  "android.file.delete": "File Delete",
  "android.file.rename": "File Rename",
  "android.proc.terminate": "Process Terminate",
  "android.proc.create": "Process Create",
  "android.preference.get": "Shared Preferences Get",
  "android.preference.put": "Shared Preferences Set",
  "android.resolver.query": "Content URI Query",
  "android.resolver.insert": "Content URI Insert",
  "android.resolver.delete": "Content URI Delete",
  "android.resolver.observe": "Content URI Observe",
  "android.service": "System Service",

  "macos.file.create": "File Create",
  "macos.file.read": "File Read",
  "macos.file.write": "File Write",
  "macos.file.delete": "File Delete",
  "macos.file.rename": "File Rename",

  "proc.attack": "Attack",
};

let labelMap = {
  op: "operation",
  remote_addr: "remote address",
  proc: "process",
  remote_port: "remote port",
  main_event: "category",
  q: "Search Query",
};

let topKeys = {
  Misc: [],
  Process: ["kind", "image", "status", "command"],
  Registry: ["op", "status"],
  Files: ["op", "status"],
  Mutex: ["op", "status"],
  Network: ["remote_addr", "proto", "proc", "remote_port"],
  "Shared Preferences": ["key"],
  "Content URI": ["uri"],
  "System Service": ["name", "command"],
};

let initialize = async () => {
  if (!EVENTS) {
    EVENTS = await fetchData();
  }
  if (!EVENTS?.length) return;
  offset = parseInt(HREF.searchParams.get("offset")) || 0;
  LIMIT = parseInt(HREF.searchParams.get("limit")) || 100;
  let filters = searchParamsToFilters(HREF.searchParams);
  let filteredEvents = [];
  if (filters.q && filters.q.length) {
    LOADER = new Loader().init(LOG, true, "Querying Events List");
    let sort = await filterEvents(filters, EVENTS);
    filteredEvents = await searchEvents(filters.q, sort);
  } else if (Object.entries(filters).length) {
    LOADER = new Loader().init(LOG, true, "Fetching Events List");
    filteredEvents = await filterEvents(filters, EVENTS);
  } else {
    LOADER = new Loader().init(LOG, true, "Fetching Events Overview");
    filteredEvents = EVENTS;
  }
  await renderEventList(filteredEvents, filters);
  await renderFilters(filteredEvents, filters);
  addRedirectClickHandlers();
};

let fetchData = async () => {
  let url = `/samples/${globalThis.page_data.sample.id}/${
    globalThis.page_data.task_id ? globalThis.page_data.task_id : globalThis.page_data.task.id
  }/analog.json`;
  let fetched = await fetch(url);
  let data = await fetched.text();
  if (!fetched.ok) {
    toggleFilters();
    showError();
    return [];
  }
  let raw = data.split(/\n/);
  return raw.flatMap((entry) => {
    if (entry.length) {
      return JSON.parse(entry);
    } else {
      return [];
    }
  });
};

let filterEvents = async (filters, data: Array<Entry>) => {
  let formatted = [];
  let category = HREF.searchParams.get("main_event");
  data.forEach(async (ev: Entry) => {
    let evCat = reMap[ev.n];
    let isMatch = true;

    if (category && evCat !== category) return;

    for (const [key, value] of Object.entries(filters)) {
      if (key == "main_event") continue;
      if (key == "q") continue;
      if (ev.d[key] === undefined) {
        isMatch = false;
      }
      if (isMatch && ev.d[key].toString() !== value.toString()) {
        isMatch = false;
      }
    }

    if (isMatch) {
      formatted.push(ev);
    }
  });
  return formatted;
};

let searchEvents = async (query: string, data: Array<Entry>) => {
  let list = [];

  data.forEach((entry) => {
    let blob = JSON.stringify(entry).toLowerCase();
    if (blob.includes(query.toLowerCase())) {
      list.push(entry);
    }
  });

  return list;
};

let renderEventList = async (data: Array<Entry>, filters) => {
  if (!data || !data.length) {
    showEmpty();
    return;
  }
  let slice = data.slice(offset * LIMIT, offset * LIMIT + LIMIT);

  if (!slice || !slice.length) {
    showEmpty();
    return;
  }
  let list = document.createElement("ul");
  LOG.innerHTML = "";

  list.classList.add("list", "scrollbox", "flex");
  list.innerHTML = HatchMap(slice, (ev) => {
    if (view === "grid") {
      return renderGrid(ev, filters);
    } else if (view === "rows") {
      return renderRow(ev, filters);
    }
  }).toString();
  LOG.appendChild(list);

  if (data.length > LIMIT) {
    addPagination(data.length, filters);
  }
  LOADER.hide();
};

let renderRow = (ev: Entry, filters) => {
  return Hatch`
    <li class="nano hover">
      <div class="hbox">
        <b>${nameMap[ev.n] || ev.n}</b>
        <div class="key-value ${view}">
          ${HatchMap(Object.entries(ev.d), (pair) => {
            let displayValue = pair[1];
            if (pair[0] === "status") {
              displayValue = makeItHex(pair[1]);
            }
            return Hatch`
            <div ${typeof pair[1] !== "object" ? Hatch`title="${pair[1]}"` : ""}>
              <div>${pair[0]}</div>
              <div class="wrap">${typeof pair[1] !== "object" ? displayValue : renderSpecial(pair)}</div>
              ${
                typeof pair[1] !== "object"
                  ? Hatch`<a class="button ${
                      filters[pair[0]] ? "secondary" : "primary"
                    } icon" data-redirect href="${getLinkie(filters, pair[0], pair[1])}"  aria-label="Filter on ${
                      pair[0]
                    }">
                  <div class="ma-icon small">
                    ${new SafeString(filters[pair[0]] ? remove : add)}
                  </div>
                </a>`
                  : ""
              }

            </div>`;
          })}
        </div>
      </div>
    </li>`;
};

let renderGrid = (ev: Entry, filters) => {
  return Hatch`
  <li class="nano hover grid">
    <div class="hbox">
        <b>${nameMap[ev.n] || ev.n}</b>
        <div>
          ${HatchMap(Object.entries(ev.d), (pair) => {
            let displayValue = pair[1];
            if (pair[0] === "status") {
              displayValue = makeItHex(pair[1]);
            }
            let event = HREF.searchParams.get("main_event");
            return Hatch`
            <b>${pair[0]}: </b>
            <span>${typeof pair[1] !== "object" ? displayValue : renderSpecial(pair)}</span>

            ${
              typeof pair[1] !== "object"
                ? Hatch`<a class="button primary icon ${
                    filters[pair[0]] ? "secondary" : "primary"
                  }" data-redirect href="${getLinkie(filters, pair[0], pair[1])}">
                <div class="ma-icon">
                  ${new SafeString(filters[pair[0]] ? remove : add)}
                </div>
              </a>`
                : ""
            }`;
          })}
          </div>
        </div>
      </div>
    </li>`;
};

let isStringsArray = (arr) => arr.every((i) => typeof i === "string");

let renderSpecial = (special) => {
  if (!special) {
    return "NA";
  }
  let value = special[1];
  if (isStringsArray(value)) {
    return value.join(" ");
  } else {
    return HatchMap(Object.entries(value), (item) => {
      return Hatch`${HatchMap(Object.entries(item[1]), (item) => {
        return Hatch`<b>${item[0]}</b>: ${item[1]} `;
      })}<br>`;
    });
  }
};

let addPagination = (total: number, filters) => {
  let amount = Math.ceil(total / LIMIT);
  let el = document.createElement("div");
  el.classList.add("pagination");
  el.innerHTML = `
    <span>${LIMIT * offset} - ${
    LIMIT * offset + LIMIT < total ? LIMIT * offset + LIMIT : total
  } of ${total} Records</span>
    <div>
      <span>${offset + 1} of ${amount} pages</span>
      <a href="${getLinkie(
        filters,
        null,
        null
      )}" aria-label="First Page" data-redirect><div class="ma-icon">${firstPage}</div></a>
      <a href="${getLinkie(filters, null, null)}&offset=${
    offset - 1 > 0 ? offset - 1 : 0
  }" data-redirect aria-label="Previous Page"><div class="ma-icon">${chevronLeft}</div></a>
      <a href="${getLinkie(filters, null, null)}&offset=${
    offset + 1 < amount ? offset + 1 : offset
  }" data-redirect aria-label="Next Page"><div class="ma-icon">${chevronRight}</div></a>
      <a href="${getLinkie(filters, null, null)}&offset=${
    amount - 1
  }" data-redirect aria-label="Last Page"><div class="ma-icon">${lastPage}</div></a>
    </div>
  `;
  LOG.appendChild(el);
};

let showEmpty = () => {
  LOG.innerHTML = Hatch`
  <div class="loader" style="display: flex;">
    <div class="loader-holder">
      <div class="loader-holder__spinner">
        <div class="ma-icon large">
          ${new SafeString(no_luggage)}
        </div>
      </div>
      <h3>No Results</h3>
      <p>Please try adjusting the search parameters.</p>
    </div>
  </div>`.toString();
};

let showError = () => {
  LOG.innerHTML = Hatch`
  <div class="loader" style="display: flex;">
    <div class="loader-holder">
      <div class="loader-holder__spinner">
        <div class="ma-icon large">
          ${new SafeString(warning)}
        </div>
      </div>
      <h3>Failed to fetch data</h3>
      <p>Please <a href="mailto:support@hatching.io?subject=Problem fetching analog">contact us</a> if the problem persists with future analysis.</p>
    </div>
  </div>`.toString();
};

let formatOverview = async (data: Array<Entry>) => {
  let keySort = {};

  data.forEach(async (ev: Entry) => {
    if (!keySort[reMap[ev.n]]) {
      keySort[reMap[ev.n]] = {
        count: 0,
        counts: [],
        events: [],
      };
    }

    keySort[reMap[ev.n]].count++;
    keySort[reMap[ev.n]].events.push(ev.d);

    if (!topKeys[reMap[ev.n]]) {
      return;
    }
    topKeys[reMap[ev.n]].forEach(async (key) => {
      let cat = keySort[reMap[ev.n]].counts.find((event: Counter) => {
        if (event.label === key) {
          return event;
        } else {
          return false;
        }
      });
      if (!cat) {
        keySort[reMap[ev.n]].counts.push({
          label: key,
          counters: {},
        });
        cat = keySort[reMap[ev.n]].counts.find((event: Counter) => {
          if (event.label === key) {
            return event;
          }
        });
      }

      if (ev.d[key] !== undefined) {
        if (!cat.counters[ev.d[key]]) {
          cat.counters[ev.d[key]] = 0;
        }
        cat.counters[ev.d[key]]++;
      }
    });
  });
  return keySort;
};

let addRedirectClickHandlers = () => {
  document.querySelectorAll("[data-redirect]").forEach((a: HTMLAnchorElement) => {
    a.addEventListener("click", async (e) => {
      e.preventDefault();
      HREF = new URL(a.href);
      history.pushState(history.state, "hello", HREF.href);
      initialize();
    });
  });
};

let searchEl = document.querySelector("form#analog-search");
searchEl.addEventListener("submit", (ev) => {
  let val = searchEl.querySelector("input").value;
  HREF.searchParams.set("q", val);
  HREF.searchParams.delete("offset");
  offset = 0;
  history.pushState(history.state, "hello", HREF);
  initialize();
  ev.preventDefault();
});

let renderFilters = async (data: Array<Entry>, filters) => {
  let selectionCounts = await formatOverview(data);
  let allCounts = await formatOverview(EVENTS);
  let container = document.querySelector("#properties-filters");
  let excludedFilters = ["main_event"];

  document.querySelector("#event_types").innerHTML = "";
  container.innerHTML = "";

  addEventFilters(filters, allCounts, selectionCounts);

  if (filters.main_event) {
    excludedFilters = await addMappedFilters(excludedFilters, selectionCounts, filters);
  } else {
    document.querySelector("#properties-title").classList.add("hidden");
  }
  addUnmappedFilters(filters, excludedFilters);
};

let getLinkie = (filters: { [k: string]: string }, key: string, value: string) => {
  let isEnabled = filters[key] ? true : false;
  let linkie = `analog?${encodeFilters(filters, key)}${
    value && !isEnabled ? `&${key}=${encodeURIComponent(value)}` : ""
  }`;
  return linkie;
};

let encodeFilters = (filters: { [k: string]: string }, exclude: string) => {
  const searchParams = new URLSearchParams();

  for (const [key, v] of Object.entries(filters)) {
    if (key !== exclude) {
      searchParams.append(key, v);
    }
  }
  return searchParams.toString();
};

let addEventFilters = (filters, allCounts, selectionCounts) => {
  let hasEvent = filters.main_event ? true : false;
  clearTooltips();
  for (let key of Object.keys(allCounts)) {
    document.querySelector("#event_types").innerHTML += Hatch`<a href="${getLinkie(filters, "main_event", null)}${
      filters["main_event"] !== key ? `&main_event=${key}` : ""
    }" class="${
      hasEvent && filters["main_event"] == key ? "blue destructible" : ""
    }" data-event-filter="${key}" data-redirect>${key}</a>`;
  }

  for (let key of Object.keys(allCounts)) {
    new Tooltip({
      target: document.querySelector(`[data-event-filter="${key}"]`),
      text: allCounts[key].count + ` Record${allCounts[key].count !== 1 ? "s" : ""}`,
    });
  }
};

let clearTooltips = () => {
  document.querySelectorAll(".tooltip").forEach((element) => {
    element.remove();
  });
};

let addUnmappedFilters = (filters, excludedFilters) => {
  let container = document.querySelector("#properties-filters");
  Object.keys(filters).forEach((filter) => {
    if (!excludedFilters?.includes(filter)) {
      container.innerHTML += Hatch`
      <div class="block active">
        <h4>${filter == "q" ? "Query" : filter}</h4>
        <div class="content">
            <a href="analog?${encodeFilters(filters, filter)}" data-redirect>
            <p>${filter === "status" ? makeItHex(filters[filter]) : filters[filter]}</p>
            <span><div class="ma-icon small">${new SafeString(close)}</div></span>
            </a>
        </div>
      </div>
      `;
    }
  });
};

let addMappedFilters = (excludedFilters, selectionCounts, filters) => {
  let container = document.querySelector("#properties-filters");

  for (const [key, value] of <any>Object.entries(selectionCounts)) {
    if (!value.counts?.length) {
      document.querySelector("#properties-title").classList.add("hidden");
    } else {
      document.querySelector("#properties-title").classList.remove("hidden");
    }
    value.counts.forEach((count) => {
      if (Object.entries(count.counters).length) {
        let active = false;
        if (!HREF.searchParams.has(count.label)) {
          active = true;
        }
        excludedFilters.push(count.label);

        container.innerHTML += Hatch`
        <div class="block ${!active ? "active" : ""}">
          <h4>${labelMap[count.label] ? labelMap[count.label] : count.label}</h4>
          <div class="content">
            ${HatchMap(Object.entries(count.counters), (item) => {
              return Hatch`<a href="${getLinkie(filters, count.label, item[0])}" data-redirect style="order:-${
                item[1]
              };">
              <p>${count.label === "status" ? makeItHex(item[0]) : item[0]}</p>
              <span>${active ? item[1] : Hatch`<div class="ma-icon small">${new SafeString(close)}</div>`}</span>
              </a>`;
            })}
          </div>
        </div>
        `;
      }
    });
    return excludedFilters;
  }
  return excludedFilters;
};

let toggleFilters = () => {
  let toggleEl = document.querySelector("#toggle_filters");
  document.querySelector("#filters").classList.toggle("hidden");
  if (toggleEl.classList.contains("primary")) {
    toggleEl.classList.remove("primary");
    toggleEl.classList.add("secondary");
  } else {
    toggleEl.classList.add("primary");
    toggleEl.classList.remove("secondary");
  }
};

let makeItHex = (input: number | string) => {
  if (typeof input === "string") {
    input = parseInt(input);
  }
  return "0x" + input.toString(16).padStart(8, "0");
};

let searchParamsToFilters = (searchParams: URLSearchParams): Record<string, string> => {
  const paramsObject: Record<string, string> = {};
  const excluded = ["offset", "limit"];

  searchParams.forEach((value, key) => {
    if (excluded.includes(key)) return;
    paramsObject[key] = value;
  });

  return paramsObject;
};

window.addEventListener("popstate", (e) => {
  HREF = new URL(window.location.href);
  initialize();
});

document.querySelectorAll("[data-view]").forEach((el) => {
  if (localStorage.getItem("analog-view-preference") === el.getAttribute("data-view")) {
    el.classList.add("primary");
    el.classList.remove("secondary");
  }
  el.addEventListener("click", () => {
    document.querySelectorAll("[data-view]").forEach((el) => {
      el.classList.add("secondary");
      el.classList.remove("primary");
    });

    el.classList.add("primary");
    el.classList.remove("secondary");

    view = el.getAttribute("data-view");
    localStorage.setItem("analog-view-preference", view);
    HREF = new URL(window.location.href);
    initialize();
  });
});

document.querySelector("#toggle_filters")?.addEventListener("click", toggleFilters);

document.querySelector(`[data-view="${view}"]`)?.classList.add("primary");
document.querySelector(`[data-view="${view}"]`)?.classList.remove("secondary");

initialize();
